import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import ApiService from './api-service';
import ApiError from '../util/ApiError';

export const INVOICE_TABLE_TEMPLATE = { name: 'Invoice table' };

const ChaseNowService = {
  async getChaseDetails({ shortCode, customerId, invoiceIds, sort }) {
    const isMaybeMulti = invoiceIds.length !== 1;
    const invoiceId = invoiceIds[0];

    const commonFragment = `
    senders {
      regular {
        from {
          id
          FirstName
          LastName
          EmailAddress
          forewarn
          signature
          verified
        }
        cc {
          id
          FirstName
          LastName
          EmailAddress
          forewarn
          signature
          verified
        }
      }
      escalated {
        from {
          id
          FirstName
          LastName
          EmailAddress
          forewarn
          signature
          verified
        }
        cc {
          id
          FirstName
          LastName
          EmailAddress
          forewarn
          signature
          verified
        }
      }
    }
    contact(ContactID: $customerId) {
      FirstName
      LastName
      ContactID
      EmailAddress
      hasInvalidMobileNumber
      hasMissingEmail
      hasWrongEmail
      hasMissingAddress
      ContactPersons {
        FirstName
        LastName
        EmailAddress
        ContactID
      }
      schedule {
        name
      }
      Phones {
        PhoneNumber
        PhoneAreaCode
        PhoneType
        PhoneCountryCode
      }
      Addresses {
        AddressLine1
        AddressLine2
        AddressLine3
        AddressLine4
        AddressType
        City
        Region
        PostalCode
        Country
        AttentionTo
      }
      recipients {
        regular {
          to {
            FirstName
            LastName
            EmailAddress
            ContactID
          }
          cc {
            FirstName
            LastName
            EmailAddress
            ContactID
          }
        }
        escalated {
          to {
            FirstName
            LastName
            EmailAddress
            ContactID
          }
          cc {
            FirstName
            LastName
            EmailAddress
            ContactID
          }
        }
      }
    }`;

    const multiQuery = `query ($customerId: String!, $sort: String!) {
      organisation {
        invoices(ContactID: $customerId, filter: { paid: false }, sort: $sort) {
          nodes {
            _id
            noChase
            InvoiceID
            InvoiceNumber
            DueDate
            AmountDue
            Total
            CurrencyCode
            invoicePdfLink
            schedule {
              name
            }
            nextChase {
              date
              singleTemplate
              multiTemplate
              template
              escalateSenders
              escalateRecipients
              channel
            }
          }
        }
        ${commonFragment}
      }
    }`;
    const singleQuery = `query ($customerId: String!, $invoiceId: String!) {
      organisation {
        invoice(InvoiceID: $invoiceId) {
          _id
          InvoiceID
          InvoiceNumber
          invoicePdfLink
          nextChase {
            date
            singleTemplate
            template
            escalateSenders
            escalateRecipients
            channel
          }
        }
        ${commonFragment}
      }
    }`;

    const body = {
      query: isMaybeMulti ? multiQuery : singleQuery,
      variables: {
        customerId,
        invoiceId,
        sort,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);

    const invoices = data.organisation.invoices?.nodes
      .filter(
        i =>
          i.AmountDue > 0 &&
          // (invoiceIds.length || !i.noChase) &&
          (!invoiceIds.length || invoiceIds.includes(i.InvoiceID)),
      )
      .map(i => ({
        ...i,
        daysOverdue: differenceInCalendarDays(new Date(), new Date(i.DueDate)),
      }));
    const isContactWithSingle = isMaybeMulti && invoices.length === 1;
    const isMulti = isMaybeMulti && !isContactWithSingle;
    const invoice = isContactWithSingle
      ? data.organisation.invoices.nodes[0]
      : data.organisation.invoice;

    return {
      contactPersons: [
        {
          FirstName: data.organisation.contact.FirstName,
          LastName: data.organisation.contact.LastName,
          EmailAddress: data.organisation.contact.EmailAddress,
          ContactID: data.organisation.contact.ContactID,
        },
      ].concat(data.organisation.contact.ContactPersons),
      isMulti,
      recipients: data.organisation.contact.recipients,
      senders: data.organisation.senders,
      nextChase: invoice?.nextChase,
      invoices: invoices || [invoice],
      contact: data.organisation.contact,
    };
  },
  async getChaseNowPreview(ShortCode, contactId, invoices, template, channel, senders, recipients) {
    const invoiceIds = invoices.map(i => i._id);
    const body = {
      query: `mutation($template: TemplateInput!, $channel: String!, $contactId: String!, $invoiceIds: [String]!, $senders: SendChaseNowSenders!, $recipients: SendChaseNowRecipients!) {
        chaseNowPreview(template: $template, channel: $channel, contactId: $contactId, invoiceIds: $invoiceIds, senders: $senders, recipients: $recipients) {
          subject
          text
          pdf
        }
      }`,
      variables: {
        template: {
          name: template.name,
          subject: template.subject,
          text: template.text,
          dateFormat: template.dateFormat,
        },
        channel,
        invoiceIds,
        contactId,
        senders,
        recipients,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);
    return data.chaseNowPreview;
  },
  async sendChaseNow(
    ShortCode,
    {
      replaceUpcomingChase,
      subject,
      text,
      channel,
      contactId,
      invoiceIds,
      attachInvoice,
      attachStatement,
      customAttachments = [],
      senders,
      recipients,
      sort,
      dateFormat,
    },
  ) {
    const isMulti = invoiceIds.length !== 1;
    if (isMulti) {
      await ChaseNowService.sendChaseNowMulti(ShortCode, {
        subject,
        text,
        channel,
        contactId,
        invoiceIds,
        attachInvoice,
        attachStatement,
        senders,
        recipients,
        customAttachments,
        sort,
        dateFormat,
      });

      return;
    }
    const body = {
      query: `mutation sendChaseNow(
          $subject: String!,
          $text: String!,
          $channel: String!,
          $invoiceId: String!,
          $attachInvoice: Boolean!,
          $attachStatement: Boolean!,
          $senders: SendChaseNowSenders!,
          $recipients: SendChaseNowRecipients!,
          $replaceUpcomingChase: Boolean,
          $customAttachments: [String],
          $dateFormat: String,
        ) {
        sendChaseNow(
          subject: $subject,
          text: $text,
          channel: $channel,
          invoiceId: $invoiceId,
          attachInvoice: $attachInvoice,
          attachStatement: $attachStatement,
          customAttachments: $customAttachments,
          senders: $senders,
          recipients: $recipients,
          replaceUpcomingChase: $replaceUpcomingChase,
          dateFormat: $dateFormat,
        )
      }`,
      variables: {
        subject,
        text,
        invoiceId: invoiceIds[0],
        channel,
        attachInvoice,
        attachStatement,
        senders,
        recipients,
        replaceUpcomingChase,
        customAttachments,
        dateFormat,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);
    if (!data?.sendChaseNow) throw new ApiError('Chaser failed to send');
  },
  async replyNow(
    ShortCode,
    {
      subject,
      text,
      channel,
      invoiceIds,
      attachInvoice,
      attachStatement,
      customAttachments = [],
      senders,
      recipients,
      dateFormat,
      replyToMessageId,
    },
  ) {
    const body = {
      query: `mutation sendReply(
          $subject: String!,
          $text: String!,
          $channel: String!,
          $invoiceIds: [String],
          $attachInvoice: Boolean!,
          $attachStatement: Boolean!,
          $senders: SendChaseNowSenders!,
          $recipients: SendChaseNowRecipients!,
          $customAttachments: [String],
          $dateFormat: String,
          $replyToMessageId: String!,
        ) {
        sendReply(
          subject: $subject,
          text: $text,
          channel: $channel,
          invoiceIds: $invoiceIds,
          attachInvoice: $attachInvoice,
          attachStatement: $attachStatement,
          customAttachments: $customAttachments,
          senders: $senders,
          recipients: $recipients,
          dateFormat: $dateFormat,
          replyToMessageId: $replyToMessageId,
        )
      }`,
      variables: {
        subject,
        text,
        invoiceIds,
        channel,
        attachInvoice,
        attachStatement,
        senders,
        recipients,
        customAttachments,
        dateFormat,
        replyToMessageId,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);
    if (!data?.sendReply) throw new ApiError('Reply failed to send');
  },
  async sendChaseNowMulti(
    ShortCode,
    {
      subject,
      text,
      channel,
      contactId,
      invoiceIds,
      attachInvoice,
      attachStatement,
      customAttachments = [],
      senders,
      recipients,
      sort,
      dateFormat,
    },
  ) {
    const body = {
      query: `mutation sendChaseNowMultiple(
          $subject: String!,
          $text: String!,
          $channel: String!,
          $contactId: String!,
          $invoiceIds: [String]!,
          $attachInvoice: Boolean,
          $attachStatement: Boolean,
          $senders: SendChaseNowSenders!,
          $recipients: SendChaseNowRecipients!,
          $customAttachments: [String],
          $sort: String!,
          $dateFormat: String,
        ) {
        sendChaseNowMultiple(
          subject: $subject,
          text: $text,
          channel: $channel,
          contactId: $contactId,
          invoiceIds: $invoiceIds,
          attachInvoices: $attachInvoice,
          attachStatement: $attachStatement,
          customAttachments: $customAttachments,
          senders: $senders,
          recipients: $recipients,
          sort: $sort,
          dateFormat: $dateFormat,
        )
      }`,
      variables: {
        subject,
        text,
        channel,
        contactId,
        invoiceIds,
        attachInvoice,
        attachStatement,
        senders,
        recipients,
        customAttachments,
        sort,
        dateFormat,
      },
    };

    const data = await ApiService.orgEndpoint(ShortCode).post('/', body);
    if (!data?.sendChaseNowMultiple) throw new ApiError('Chaser failed to send');
  },

  async uploadAttachment(ShortCode, file, onProgress = () => {}, cancelToken) {
    const ops = {
      query: `mutation($file: Upload!) {
        uploadCustomAttachment(file: $file)
      }
      `,
      variables: { file: null },
    };
    const map = {
      '0': ['variables.file'],
    };

    const formData = new FormData();
    formData.append('operations', JSON.stringify(ops));
    formData.append('map', JSON.stringify(map));
    formData.append(0, file);

    const data = await ApiService.orgEndpoint(ShortCode, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).post('/', formData, { onUploadProgress: onProgress, cancelToken });
    return data.uploadCustomAttachment;
  },
};

export default ChaseNowService;
